/**
 * MyTaskRow
 * Extended task row for the My Tasks personal inbox.
 * Shows: workflow badge | task name — client | pin indicator
 *        assignee | SLA pill | status badge | date MM-DD | View Details
 *
 * The whole row is a button: clicking it (or Enter/Space when focused) opens
 * the task detail panel, same as the "View Details" link.
 */

import { Pin } from 'lucide-react';
import styled from 'styled-components';
import {
  WorkflowTypeBadge,
  SLAPill,
  StatusBadge,
} from '../../components/tasks';
import type { Task, SLABand } from '../../types/tasks';

// ============================================================================
// Types
// ============================================================================

export interface MyTaskRowProps {
  task: Task;
  onTogglePin?: (taskId: string) => void;
  onViewDetails?: (taskId: string) => void;
}

// ============================================================================
// Helpers
// ============================================================================

function formatMonthDay(isoDate: string | null): string | null {
  if (!isoDate) return null;
  const d = new Date(isoDate);
  if (isNaN(d.getTime())) return null;
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${month}-${day}`;
}

function fullName(user: { firstName: string; lastName: string } | null): string {
  return user ? `${user.firstName} ${user.lastName}` : 'Unassigned';
}

function isValidSLABand(band: string): band is SLABand {
  return band === 'green' || band === 'yellow' || band === 'red';
}

// ============================================================================
// Styled components
// ============================================================================

const Row = styled.article<{ $clickable: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[4]}`};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borders.light};
  background: ${({ theme }) => theme.colors.backgrounds.white};
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  transition: background ${({ theme }) => theme.transitions.fast};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.backgrounds.page};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary.base};
    outline-offset: -2px;
  }
`;

const LeftSection = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  overflow: hidden;
`;

const TaskName = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ClientSeparator = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  white-space: nowrap;
`;

const PinButton = styled.button`
  display: inline-flex;
  align-items: center;
  background: none;
  border: none;
  padding: ${({ theme }) => theme.spacing[1]};
  cursor: pointer;
  color: ${({ theme }) => theme.colors.accent.gold};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  flex-shrink: 0;
  transition: color ${({ theme }) => theme.transitions.fast},
    opacity ${({ theme }) => theme.transitions.fast};

  &:hover {
    opacity: 0.75;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary.base};
    outline-offset: 2px;
  }
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  flex-shrink: 0;
`;

const AssigneeText = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.secondary};
  white-space: nowrap;
`;

const DateText = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.light};
  white-space: nowrap;
  min-width: 36px;
  text-align: right;
`;

const ViewDetailsLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.primary.base};
  cursor: pointer;
  white-space: nowrap;
  transition: color ${({ theme }) => theme.transitions.fast};

  &:hover {
    color: ${({ theme }) => theme.colors.primary.dark};
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary.base};
    outline-offset: 2px;
    border-radius: ${({ theme }) => theme.borderRadius.sm};
  }
`;

// ============================================================================
// Component
// ============================================================================

export function MyTaskRow({ task, onTogglePin, onViewDetails }: MyTaskRowProps) {
  const displayDate = formatMonthDay(task.dueDate ?? task.scheduledDate);
  const assigneeName = fullName(task.assignedTo);
  const clientName = task.workflowInstance?.clientMember
    ? `${task.workflowInstance.clientMember.firstName} ${task.workflowInstance.clientMember.lastName}`
    : null;

  const sla =
    task.slaContext && isValidSLABand(task.slaContext.slaBand)
      ? {
          daysRemaining: task.slaContext.daysRemaining,
          slaBand: task.slaContext.slaBand as SLABand,
        }
      : null;

  const handlePinClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onTogglePin?.(task.id);
  };

  // The whole row activates onViewDetails. Nested controls (pin, View Details)
  // stopPropagation so they never double-fire.
  const handleRowActivate = onViewDetails
    ? () => onViewDetails(task.id)
    : undefined;

  const handleRowKeyDown = handleRowActivate
    ? (e: React.KeyboardEvent) => {
        // Enter/Space on a nested control (pin, View Details) is that control's
        // to handle — only act when the row itself has focus.
        if (e.target !== e.currentTarget) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleRowActivate();
        }
      }
    : undefined;

  return (
    <Row
      $clickable={!!handleRowActivate}
      role={handleRowActivate ? 'button' : undefined}
      tabIndex={handleRowActivate ? 0 : undefined}
      onClick={handleRowActivate}
      onKeyDown={handleRowKeyDown}
      aria-label={`Task: ${task.name}${clientName ? ` for ${clientName}` : ''}`}
    >
      <LeftSection>
        {task.workflowInstance?.workflowType && (
          <WorkflowTypeBadge workflowType={task.workflowInstance.workflowType} />
        )}

        <TaskName title={task.name}>{task.name}</TaskName>

        {clientName && (
          <ClientSeparator aria-hidden="true">
            &mdash; {clientName}
          </ClientSeparator>
        )}

        {task.pinned && (
          <PinButton
            type="button"
            onClick={handlePinClick}
            aria-label={`Unpin task: ${task.name}`}
            title="Pinned — click to unpin"
          >
            <Pin size={12} strokeWidth={2.5} aria-hidden="true" />
          </PinButton>
        )}
      </LeftSection>

      <RightSection>
        <AssigneeText>{assigneeName}</AssigneeText>

        {sla && (
          <SLAPill
            daysRemaining={sla.daysRemaining}
            slaBand={sla.slaBand}
            compact
          />
        )}

        <StatusBadge status={task.status} size="sm" />

        {displayDate && <DateText>{displayDate}</DateText>}
        {onViewDetails && (
          <ViewDetailsLink
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onViewDetails(task.id);
            }}
            aria-label={`View details for ${task.name}`}
          >
            View Details &rsaquo;
          </ViewDetailsLink>
        )}
      </RightSection>
    </Row>
  );
}

export default MyTaskRow;
